import type {
  Attendance,
  AwayStatus,
  BoardResponse,
  BusinessTrip,
  DeskOrientation,
  Employee,
  EmployeeDocument,
  EmployeeProfile,
  Holiday,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
  MeetingReservation,
  MeetingStatusResponse,
  MeResponse,
  Notice,
  NoticeDetail,
  ResourceCategory,
  StatsSummary,
} from '@dxtech/shared';

export interface ProfileBasic {
  name: string; dept: string; title: string; email: string; birthday: string; phone: string; address: string; hireDate: string;
}
export interface CompanyInfo {
  name: string; bizNo: string; ceo: string; corpNo: string; openDate: string; address: string;
  bizType: string[]; bizItem: string[]; taxEmail: string;
}
export interface CompanyDoc { id: string; name: string; size: number; uploadedAt: string; }
export interface ProfileResponse {
  owner: string; editable: boolean; basic: ProfileBasic | null; profile: EmployeeProfile | null;
}
import { acquireApiToken } from '../auth/authService.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await acquireApiToken();
  // 본문이 있을 때만 content-type 지정 (본문 없는 POST 에 application/json 을 붙이면
  // Fastify 가 "Body cannot be empty" 로 거부함 → 출근/퇴근/승인 등 bodyless POST 오류 방지)
  const hasBody = init.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `API ${res.status} ${path}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) msg = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });

export const api = {
  me: () => request<MeResponse>('/me'),
  // 본인 프로필(생일/주소/연락처)
  meProfile: () => request<{ profile: Employee | null }>('/me/profile'),
  updateMyProfile: (body: { birthday?: string; birthdayType?: '양력' | '음력'; address?: string; phone?: string }) =>
    request<{ ok: boolean; profile?: Employee | null }>('/me/profile', { method: 'POST', body: JSON.stringify(body) }),

  // 출퇴근
  attendanceToday: (date?: string) =>
    request<{ date: string; item: Attendance | null }>(`/attendance${date ? `?date=${date}` : ''}`),
  checkIn: () => post<{ ok: boolean; attendance?: Attendance }>('/attendance/checkin'),
  checkOut: () => post<{ ok: boolean; attendance?: Attendance }>('/attendance/checkout'),
  cancelCheckOut: () => post<{ ok: boolean; attendance?: Attendance }>('/attendance/checkout/cancel'),
  attendanceAdmin: (from: string, to: string) =>
    request<{ from: string; to: string; items: Attendance[] }>(
      `/attendance/admin?from=${from}&to=${to}`,
    ),
  // 관리자 근태 보정(upsert). 시각은 UTC ISO 또는 null. (Admin 전용, Status='보정')
  correctAttendance: (body: { upn: string; date: string; checkInAt?: string | null; checkOutAt?: string | null; note?: string | null }) =>
    post<{ ok: boolean; attendance?: Attendance }>('/attendance/admin/correct', body),

  // 자리비움
  awayByDate: (date: string) =>
    request<{ date: string; items: AwayStatus[] }>(`/away?date=${date}`),
  createAway: (body: { startTime: string; endTime: string; reason?: string }) =>
    post<{ ok: boolean }>('/away', body),

  // 보드
  board: (date: string) => request<BoardResponse>(`/board?date=${date}`),

  // 휴가
  leaveMine: () => request<{ items: LeaveRequest[] }>('/leave/mine'),
  leaveBalance: () => request<{ balance: LeaveBalance | null }>('/leave/balance'),
  createLeave: (body: { type: LeaveType; startDate: string; endDate: string; reason?: string }) =>
    post<{ ok: boolean }>('/leave', body),
  cancelLeave: (id: string) => post<{ ok: boolean }>(`/leave/${id}/cancel`),
  leavePending: () => request<{ items: LeaveRequest[] }>('/leave/pending'),
  leaveByPerson: (upn: string) =>
    request<{ items: LeaveRequest[] }>(`/leave/admin?upn=${encodeURIComponent(upn)}`),
  approveLeave: (id: string) => post<{ ok: boolean }>(`/leave/${id}/approve`),
  rejectLeave: (id: string, comment: string) => post<{ ok: boolean }>(`/leave/${id}/reject`, { comment }),

  // 통계 / 설정
  statsSummary: (date?: string) =>
    request<StatsSummary>(`/stats/summary${date ? `?date=${date}` : ''}`),
  config: () => request<{ config: Record<string, unknown> }>('/config'),

  // 전체 임직원 (관리자)
  employees: () => request<{ items: Employee[] }>('/employees'),

  // 좌석 배정/해제 (관리자) — WAF 회피 위해 POST
  assignSeat: (seatId: string, upn: string) => post<{ ok: boolean }>('/seats/assign', { seatId, upn }),
  unassignSeat: (seatId: string) => post<{ ok: boolean }>('/seats/unassign', { seatId }),
  // 좌석 평면도 조회(위치 override + 커스텀 좌석 + 삭제 목록)/이동/추가/삭제 (관리자)
  seatLayout: () =>
    request<{
      overrides: Record<string, { x: number; y: number; orientation: DeskOrientation }>;
      customDesks: { id: string; label: string; zone: '업무공간' | '연구소'; x: number; y: number; orientation: DeskOrientation }[];
      deletedIds: string[];
    }>('/seats/layout'),
  moveSeat: (seatId: string, x: number, y: number, orientation: DeskOrientation) =>
    post<{ ok: boolean }>('/seats/move', { seatId, x, y, orientation }),
  createSeat: (label: string, zone: '업무공간' | '연구소', x: number, y: number, orientation: DeskOrientation) =>
    post<{ ok: boolean; id: string }>('/seats/create', { label, zone, x, y, orientation }),
  deleteSeat: (seatId: string) => post<{ ok: boolean }>('/seats/delete', { seatId }),

  // 회의실 현황/예약 (전 임직원)
  meetings: (date: string) => request<MeetingStatusResponse>(`/meetings?date=${date}`),
  meetingsUpcoming: () => request<{ items: MeetingReservation[] }>('/meetings/upcoming'),
  createMeeting: (body: { roomId: string; date: string; startTime: string; endTime: string; topic?: string }) =>
    post<{ ok: boolean }>('/meetings', body),
  cancelMeeting: (id: string) => post<{ ok: boolean }>(`/meetings/${id}/cancel`),
  endMeeting: (id: string) => post<{ ok: boolean }>(`/meetings/${id}/end`),

  // 출장
  tripsMine: () => request<{ items: BusinessTrip[] }>('/trips/mine'),
  createTrip: (body: { startDate: string; endDate: string; location: string; detail?: string }) =>
    post<{ ok: boolean }>('/trips', body),
  cancelTrip: (id: string) => post<{ ok: boolean }>(`/trips/${id}/cancel`),

  // 임직원 증명서 문서 (본인 업로드/삭제, 본인+관리자 열람)
  docs: (upn?: string) =>
    request<{ items: EmployeeDocument[]; owner: string }>(`/docs${upn ? `?upn=${encodeURIComponent(upn)}` : ''}`),
  uploadDoc: (body: { upn?: string; category: string; fileName: string; contentBase64: string; contentType?: string }) =>
    post<{ ok: boolean }>('/docs/upload', body),
  deleteDoc: (id: string) => post<{ ok: boolean }>('/docs/delete', { id }),

  // 개인 프로파일(이력) — 본인 편집, 본인+관리자 열람/편집
  profile: (upn?: string) => request<ProfileResponse>(`/profile${upn ? `?upn=${encodeURIComponent(upn)}` : ''}`),
  saveProfile: (body: EmployeeProfile & { upn?: string }) => post<{ ok: boolean }>('/profile', body),
  // 다운로드: 인증 헤더가 필요하므로 fetch 후 blob 다운로드 트리거
  downloadDoc: async (id: string, fileName: string): Promise<void> => {
    const token = await acquireApiToken();
    const res = await fetch(`${API_BASE}/docs/content?id=${encodeURIComponent(id)}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('다운로드에 실패했습니다.');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // 회사 정보 / 자료실 문서(외부 제출·내부 문서 양식). category 미지정 시 external.
  companyInfo: () => request<{ info: CompanyInfo }>('/company/info'),
  companyDocs: (category: ResourceCategory = 'external') =>
    request<{ items: CompanyDoc[] }>(`/company/docs?category=${category}`),
  uploadCompanyDoc: (body: { category?: ResourceCategory; fileName: string; contentBase64: string; contentType?: string }) =>
    post<{ ok: boolean }>('/company/docs/upload', body),
  deleteCompanyDoc: (id: string) => post<{ ok: boolean }>('/company/docs/delete', { id }),
  downloadCompanyDoc: async (id: string, fileName: string): Promise<void> => {
    const token = await acquireApiToken();
    const res = await fetch(`${API_BASE}/company/docs/content?id=${encodeURIComponent(id)}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('다운로드에 실패했습니다.');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // 공지사항 (조회/열람 전직원, 등록/수정/삭제 Admin)
  notices: (scope: 'upcoming' | 'all' = 'upcoming') =>
    request<{ items: Notice[] }>(`/notices?scope=${scope}`),
  noticeDetail: (id: string) => request<{ notice: NoticeDetail }>(`/notices/${id}`),
  createNotice: (body: { title: string; bodyHtml: string; noticeDate: string; pinned?: boolean }) =>
    post<{ ok: boolean; notice?: Notice }>('/notices', body),
  updateNotice: (id: string, body: { title: string; bodyHtml: string; noticeDate: string; pinned?: boolean }) =>
    post<{ ok: boolean; notice?: Notice }>(`/notices/${id}`, body),
  deleteNotice: (id: string) => post<{ ok: boolean }>(`/notices/${id}/delete`, {}),

  // 공휴일
  holidays: () => request<{ items: Holiday[] }>('/holidays'),
  createHoliday: (body: { date: string; name: string }) => post<{ ok: boolean }>('/holidays', body),
  deleteHoliday: (id: string) => request<{ ok: boolean }>(`/holidays/${id}`, { method: 'DELETE' }),
};
